/** Pul bo'limi oynalari: hisob (ochish/tahrirlash), kirim/chiqim/tuzatish, yozuvni tahrirlash, o'tkazma, kurs, qoldiqni solishtirish. */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { cn, dayjs } from "@/lib/utils";
import { useAppSettings } from "@/features/drivers/api";
import { useBudgetBranches, payError } from "@/features/budget/api";
import { useCreateMove, useCurrentRate, useReconcile, useRestoreAccount, useSaveAccount, useSetRate, useTransfer, useUpdateMove } from "@/features/finance/api";
import { convert, fmt, isLinked, nowTime, num, timeText } from "@/features/finance/money";
import { categoryRate, pctError, withCommission, type CommissionRates } from "@/features/finance/commission";
import { CommissionField } from "@/features/finance/CommissionField";
import { ACCOUNT_KINDS, type AccountKind, type Currency, type MoneyAccount, type Transaction } from "@/features/finance/types";
import { Button } from "@/components/ui/button";
import { Field, Input, NumberInput, Select, Textarea, TimeInput } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

const today = () => dayjs().format("YYYY-MM-DD");
const MAX_RECEIPT_MB = 10;

/** Server xatosi: maydonga tegishli bo'lsa -- maydon ostida, bo'lmasa toast */
function useErrors() {
  const { t: tc } = useTranslation();
  const [errors, setErrors] = useState<Record<string, string>>({});
  function handle(err: unknown) {
    if (err instanceof ApiError && err.fields && Object.keys(err.fields).length) {
      setErrors(Object.fromEntries(Object.entries(err.fields).map(([k, v]) => [k, v.join(" ")])));
      if (err.message && !err.fields.amount && !err.fields.date) toast.error(err.message);
      return;
    }
    toast.error(payError(err, tc, tc("toast.failed")));
  }
  return { errors, setErrors, handle };
}

// ---- Hisob ochish / tahrirlash (owner) ----

export function AccountModal({ open, onOpenChange, account }: { open: boolean; onOpenChange: (o: boolean) => void; account?: MoneyAccount | null }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const save = useSaveAccount();
  const branches = useBudgetBranches();
  const { errors, setErrors, handle } = useErrors();
  const [f, setF] = useState({ name: "", kind: "cash" as AccountKind, currency: "UZS" as Currency, bank: "", card_last4: "", branch: "", opening_balance: "", opening_date: today(), note: "" });
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setF(
      account
        ? {
            name: account.name, kind: account.kind, currency: account.currency, bank: account.bank, card_last4: account.card_last4,
            branch: account.branch ? String(account.branch) : "", opening_balance: String(num(account.opening_balance)), opening_date: account.opening_date, note: account.note,
          }
        : { name: "", kind: "cash", currency: "UZS", bank: "", card_last4: "", branch: "", opening_balance: "", opening_date: today(), note: "" },
    );
  }, [open, account, setErrors]);

  async function submit() {
    try {
      await save.mutateAsync({
        id: account?.id,
        version: account?.version,
        name: f.name.trim(),
        kind: f.kind,
        currency: f.currency,
        bank: f.kind === "cash" ? "" : f.bank.trim(),
        card_last4: f.kind === "cash" ? "" : f.card_last4.trim(),
        branch: f.branch ? Number(f.branch) : null,
        opening_balance: f.opening_balance || "0",
        opening_date: f.opening_date,
        note: f.note,
      });
      toast.success(account ? t("money.accountSaved") : t("money.accountCreated", { name: f.name.trim() }));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) onOpenChange(false);
      handle(err);
    }
  }

  const locked = !!account?.has_transactions;
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={account ? t("money.editAccount") : t("money.newAccount")}
      description={t("money.accountDesc")}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={save.isPending} disabled={!f.name.trim()} onClick={() => void submit()}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t("money.f.name")} required error={errors.name} className="sm:col-span-2">
          <Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder={t("money.f.namePh")} maxLength={80} />
        </Field>
        <Field label={t("money.f.kind")}>
          <Select value={f.kind} onChange={(e) => set({ kind: e.target.value as AccountKind })}>
            {ACCOUNT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`money.accountKinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("money.f.currency")} error={errors.currency} hint={locked ? t("money.f.currencyLocked") : undefined}>
          <Select value={f.currency} disabled={locked} onChange={(e) => set({ currency: e.target.value as Currency })}>
            <option value="UZS">UZS</option>
            <option value="USD">USD</option>
          </Select>
        </Field>
        {f.kind !== "cash" && (
          <>
            <Field label={t("money.f.bank")}>
              <Input value={f.bank} onChange={(e) => set({ bank: e.target.value })} placeholder={t("money.f.bankPh")} maxLength={60} />
            </Field>
            <Field label={t("money.f.last4")} error={errors.card_last4} hint={t("money.f.last4Hint")}>
              <Input value={f.card_last4} onChange={(e) => set({ card_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })} inputMode="numeric" placeholder="4821" />
            </Field>
          </>
        )}
        <Field label={t("money.f.branch")} hint={t("money.f.branchHint")} className="sm:col-span-2">
          <Select value={f.branch} onChange={(e) => set({ branch: e.target.value })}>
            <option value="">{t("money.f.company")}</option>
            {(branches.data ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("money.f.opening", { currency: f.currency })} error={errors.opening_balance}>
          <NumberInput decimals={2} min={0} value={f.opening_balance} onChange={(e) => set({ opening_balance: e.target.value })} placeholder="0" />
        </Field>
        <Field label={t("money.f.openingDate")} error={errors.opening_date}>
          <Input type="date" value={f.opening_date} max={today()} onChange={(e) => set({ opening_date: e.target.value })} />
        </Field>
        <Field label={t("money.f.note")} className="sm:col-span-2">
          <Input value={f.note} onChange={(e) => set({ note: e.target.value })} maxLength={200} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Kirim / chiqim / qoldiqni tuzatish ----

export type MoveKind = "income" | "expense" | "adjustment";

export function MoveModal({
  open,
  onOpenChange,
  kind,
  accounts,
  defaultAccount,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  kind: MoveKind;
  accounts: MoneyAccount[];
  defaultAccount?: number | null;
}) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const create = useCreateMove();
  const settings = useAppSettings();
  const branches = useBudgetBranches();
  const { errors, setErrors, handle } = useErrors();
  // Kirimda kategoriya ixtiyoriy (o'z ro'yxati), chiqimda majburiy; komissiya foizi kategoriyadan qo'yiladi (2026-10-25)
  const categories = ((kind === "income" ? settings.data?.income_categories : settings.data?.expense_categories) as string[] | undefined) ?? [];
  const rates = settings.data?.commission_rates as CommissionRates | undefined;
  const moneyDir = kind === "income" ? "in" : "out";
  const [f, setF] = useState({ account: "", amount: "", date: today(), time: nowTime(), category: "", branch: "", description: "", counterparty: "", direction: "in" as "in" | "out", commission: "" });
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  const chosen = accounts.find((a) => String(a.id) === f.account);
  // Chek: Xarajatlar formasi bilan bir qoida -- sozlamada majburiy bo'lsa cheksiz chiqim yozilmaydi (backend ham tekshiradi)
  const [file, setFile] = useState<File | null>(null);
  const receiptRequired = kind === "expense" && (settings.data?.expense_receipt_required as boolean | undefined) !== false;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFile(null);
    const first = accounts.find((a) => a.id === defaultAccount) ?? accounts[0];
    setF({ account: first ? String(first.id) : "", amount: "", date: today(), time: nowTime(), category: "", branch: "", description: "", counterparty: "", direction: "in", commission: "" });
  }, [open, accounts, defaultAccount, setErrors]);

  function pickFile(picked: File | null) {
    if (picked && picked.size > MAX_RECEIPT_MB * 1024 * 1024) {
      setErrors((e) => ({ ...e, receipt: t("expenses.modal.tooLarge", { mb: MAX_RECEIPT_MB }) }));
      return;
    }
    setErrors((e) => ({ ...e, receipt: "" }));
    setFile(picked);
  }

  async function submit() {
    if (!chosen) return;
    try {
      await create.mutateAsync({
        account: chosen.id, kind, amount: f.amount, date: f.date, time: f.time || undefined, description: f.description.trim(), counterparty: f.counterparty.trim(),
        category: kind !== "adjustment" ? f.category : undefined,
        branch: f.branch ? Number(f.branch) : undefined,
        direction: kind === "adjustment" ? f.direction : undefined,
        commission_pct: kind !== "adjustment" ? f.commission : undefined,
        receipt: kind === "expense" ? file : null,
      });
      toast.success(t(`money.saved.${kind}`, { amount: fmt(f.amount, chosen.currency), account: chosen.name }));
      onOpenChange(false);
    } catch (err) {
      handle(err);
    }
  }

  const needBranch = kind === "expense" && chosen && !chosen.branch;
  // Qoldiq manfiy bo'lmaydi (2026-10-18): chiqim va "tuzatish -" qoldiqdan oshmaydi (backend ham tekshiradi)
  const outgoing = kind === "expense" || (kind === "adjustment" && f.direction === "out");
  const withFee = kind === "adjustment" ? num(f.amount) : withCommission(moneyDir, num(f.amount), f.commission).net;  // chiqimda komissiya bilan
  const short = chosen && outgoing ? Math.max(0, withFee - num(chosen.balance)) : 0;
  const valid = !!chosen && num(f.amount) > 0 && short === 0 && !pctError(f.commission) && (kind !== "expense" || (!!f.category && (!needBranch || !!f.branch) && (!receiptRequired || !!file)));
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t(`money.moveTitle.${kind}`)}
      description={t(`money.moveDesc.${kind}`)}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={create.isPending} disabled={!valid} onClick={() => void submit()}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label={t("money.f.account")} error={errors.account}>
          <Select value={f.account} onChange={(e) => set({ account: e.target.value })}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {fmt(a.balance, a.currency)}
              </option>
            ))}
          </Select>
        </Field>
        {kind === "adjustment" && (
          <Field label={t("money.f.direction")}>
            <Select value={f.direction} onChange={(e) => set({ direction: e.target.value as "in" | "out" })}>
              <option value="in">{t("money.f.adjIn")}</option>
              <option value="out">{t("money.f.adjOut")}</option>
            </Select>
          </Field>
        )}
        <Field label={t("money.f.amount", { currency: chosen?.currency ?? "" })} required error={errors.amount || (short > 0 && chosen ? t("pay.insufficient", { short: fmt(short, chosen.currency) }) : undefined)}>
          <NumberInput decimals={2} min={0} value={f.amount} onChange={(e) => set({ amount: e.target.value })} autoFocus />
        </Field>
        <Field label={t("money.f.date")} error={errors.date || errors.time}>
          <div className="flex gap-2">
            <Input type="date" value={f.date} max={today()} onChange={(e) => set({ date: e.target.value })} />
            <TimeInput seconds value={f.time} onChange={(e) => set({ time: e.target.value })} aria-label={t("money.f.time")} />
          </div>
        </Field>
        {kind !== "adjustment" && (kind === "expense" || categories.length > 0) && (
          <Field label={t("money.f.category")} required={kind === "expense"} error={errors.category}>
            <Select value={f.category} onChange={(e) => set({ category: e.target.value, commission: categoryRate(rates, moneyDir, e.target.value) })}>
              <option value="">{t(kind === "expense" ? "money.f.chooseCategory" : "money.f.noCategory")}</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {kind !== "adjustment" && chosen && (
          <CommissionField direction={moneyDir} amount={num(f.amount)} currency={chosen.currency} value={f.commission} onChange={(v) => set({ commission: v })} error={errors.commission_pct} />
        )}
        {(kind === "expense" || kind === "income") && (
          <Field label={t("money.f.branch")} required={needBranch} error={errors.branch} hint={chosen?.branch ? t("money.f.branchFromAccount", { branch: chosen.branch_name }) : undefined}>
            <Select value={f.branch} onChange={(e) => set({ branch: e.target.value })}>
              <option value="">{chosen?.branch_name ?? t("money.f.noBranch")}</option>
              {(branches.data ?? []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {kind === "income" && (
          <Field label={t("money.f.counterparty")}>
            <Input value={f.counterparty} onChange={(e) => set({ counterparty: e.target.value })} placeholder={t("money.f.counterpartyPh")} maxLength={120} />
          </Field>
        )}
        <Field label={t("money.f.description")}>
          <Textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={2} maxLength={200} />
        </Field>
        {kind === "expense" && (
          <Field label={t("expenses.modal.receipt")} required={receiptRequired} error={errors.receipt}>
            <label
              className={cn(
                "flex cursor-pointer flex-col items-center justify-center rounded-[10px] border border-dashed px-4 py-4 text-center text-[12px]",
                errors.receipt ? "border-danger" : "border-border hover:border-primary",
              )}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                pickFile(e.dataTransfer.files[0] ?? null);
              }}
            >
              <input type="file" className="sr-only" accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf" onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
              <span>
                {t("expenses.modal.dropHere")} <span className="text-primary">{t("expenses.modal.browse")}</span>
              </span>
              <span className="mt-0.5 text-muted">{t("expenses.modal.fileTypes", { mb: MAX_RECEIPT_MB })}</span>
            </label>
            {file && (
              <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-fill px-3 py-1 text-[12px]">
                {file.name} · {Math.max(1, Math.round(file.size / 1024))} KB
                <button type="button" className="text-muted hover:text-ink" onClick={() => setFile(null)} aria-label={t("expenses.modal.removeFile")}>
                  ×
                </button>
              </span>
            )}
          </Field>
        )}
      </div>
    </Modal>
  );
}

// ---- Yozuvni tahrirlash ----

export function EditMoveModal({ tx, onOpenChange }: { tx: Transaction | null; onOpenChange: (o: boolean) => void }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const update = useUpdateMove();
  const { errors, setErrors, handle } = useErrors();
  const [f, setF] = useState({ description: "", counterparty: "", amount: "", date: "", time: "", commission: "" });
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  const linked = tx ? isLinked(tx) : true;
  const income = tx?.kind === "income";

  useEffect(() => {
    if (!tx) return;
    setErrors({});
    // Komissiyali kirimda summa -- asosiy (komissiyadan oldin), net serverda qayta hisoblanadi
    setF({ description: tx.description, counterparty: tx.counterparty, amount: String(num(tx.base_amount ?? tx.amount)), date: tx.date, time: timeText(tx.at),
           commission: tx.commission_pct ? String(num(tx.commission_pct)) : "" });
  }, [tx, setErrors]);

  async function submit() {
    if (!tx) return;
    try {
      await update.mutateAsync({
        id: tx.id, version: tx.version, description: f.description, counterparty: f.counterparty, time: f.time,
        ...(linked ? {} : { amount: f.amount, date: f.date }),
        ...(income ? { commission_pct: f.commission } : {}),
      });
      toast.success(tc("toast.saved"));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) onOpenChange(false);
      handle(err);
    }
  }

  return (
    <Modal
      open={!!tx}
      onOpenChange={onOpenChange}
      title={t("money.editMove")}
      description={linked ? t("money.editLinkedHint") : undefined}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={update.isPending} disabled={pctError(f.commission)} onClick={() => void submit()}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {!linked && (
          <>
            <Field label={t("money.f.amount", { currency: tx?.currency ?? "" })} error={errors.amount}>
              <NumberInput decimals={2} min={0} value={f.amount} onChange={(e) => set({ amount: e.target.value })} />
            </Field>
            {income && tx && (
              <CommissionField direction="in" amount={num(f.amount)} currency={tx.currency} value={f.commission} onChange={(v) => set({ commission: v })} error={errors.commission_pct} />
            )}
            <Field label={t("money.f.date")} error={errors.date || errors.time}>
              <div className="flex gap-2">
                <Input type="date" value={f.date} max={today()} onChange={(e) => set({ date: e.target.value })} />
                <TimeInput seconds value={f.time} onChange={(e) => set({ time: e.target.value })} aria-label={t("money.f.time")} />
              </div>
            </Field>
          </>
        )}
        <Field label={t("money.f.counterparty")}>
          <Input value={f.counterparty} onChange={(e) => set({ counterparty: e.target.value })} maxLength={120} />
        </Field>
        <Field label={t("money.f.description")}>
          <Textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={2} maxLength={200} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- O'tkazma ----

export function TransferModal({ open, onOpenChange, accounts, from }: { open: boolean; onOpenChange: (o: boolean) => void; accounts: MoneyAccount[]; from?: number | null }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const transfer = useTransfer();
  const rate = useCurrentRate(open);
  const { errors, setErrors, handle } = useErrors();
  const [f, setF] = useState({ from: "", to: "", amount: "", received: "", date: today(), time: nowTime(), description: "" });
  const [receivedTouched, setReceivedTouched] = useState(false);
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  const src = accounts.find((a) => String(a.id) === f.from);
  const dst = accounts.find((a) => String(a.id) === f.to);
  const cross = !!src && !!dst && src.currency !== dst.currency;

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setReceivedTouched(false);
    const a = accounts.find((x) => x.id === from) ?? accounts[0];
    const b = accounts.find((x) => x.id !== a?.id);
    setF({ from: a ? String(a.id) : "", to: b ? String(b.id) : "", amount: "", received: "", date: today(), time: nowTime(), description: "" });
  }, [open, accounts, from, setErrors]);

  // Har xil valyuta: tushadigan summa kurs bo'yicha taklif qilinadi (o'zgartirsa bo'ladi -- bank kursi boshqacha bo'lishi mumkin)
  const suggested = useMemo(() => (cross && src && dst ? convert(num(f.amount), src.currency, dst.currency, rate) : null), [cross, src, dst, f.amount, rate]);
  const received = receivedTouched ? f.received : suggested !== null && num(f.amount) > 0 ? String(suggested) : "";

  async function submit() {
    if (!src || !dst) return;
    try {
      await transfer.mutateAsync({ from_account: src.id, to_account: dst.id, amount: f.amount, received: cross ? received : undefined, date: f.date, time: f.time || undefined, description: f.description.trim() });
      toast.success(t("money.transferred", { from: src.name, to: dst.name }));
      onOpenChange(false);
    } catch (err) {
      handle(err);
    }
  }

  const short = src ? Math.max(0, num(f.amount) - num(src.balance)) : 0; // qoldiqdan ortiq o'tkazib bo'lmaydi
  const valid = !!src && !!dst && src.id !== dst.id && num(f.amount) > 0 && short === 0 && (!cross || num(received) > 0);
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("money.transferTitle")}
      description={t("money.transferDesc")}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={transfer.isPending} disabled={!valid} onClick={() => void submit()}>
            {t("money.transferDo")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("money.f.from")} error={errors.from_account}>
            <Select value={f.from} onChange={(e) => set({ from: e.target.value })}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {a.currency}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("money.f.to")} error={errors.to_account}>
            <Select value={f.to} onChange={(e) => set({ to: e.target.value })}>
              {accounts
                .filter((a) => String(a.id) !== f.from)
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} · {a.currency}
                  </option>
                ))}
            </Select>
          </Field>
        </div>
        <Field label={t("money.f.amount", { currency: src?.currency ?? "" })} required error={errors.amount || (short > 0 && src ? t("pay.insufficient", { short: fmt(short, src.currency) }) : undefined)}>
          <NumberInput decimals={2} min={0} value={f.amount} onChange={(e) => set({ amount: e.target.value })} autoFocus />
        </Field>
        <Field label={t("money.f.date")} error={errors.date || errors.time}>
          <div className="flex gap-2">
            <Input type="date" value={f.date} max={today()} onChange={(e) => set({ date: e.target.value })} />
            <TimeInput seconds value={f.time} onChange={(e) => set({ time: e.target.value })} aria-label={t("money.f.time")} />
          </div>
        </Field>
        {cross && (
          <Field label={t("money.f.received", { currency: dst?.currency ?? "" })} required error={errors.received} hint={rate ? t("money.f.receivedHint", { rate: rate.toLocaleString("en-US") }) : t("pay.noRate")}>
            <NumberInput decimals={2} min={0}
              value={received}
              onChange={(e) => {
                setReceivedTouched(true);
                set({ received: e.target.value });
              }}
            />
          </Field>
        )}
        <Field label={t("money.f.description")}>
          <Input value={f.description} onChange={(e) => set({ description: e.target.value })} maxLength={200} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Kurs (owner) ----

export function RateModal({ open, onOpenChange, current }: { open: boolean; onOpenChange: (o: boolean) => void; current: number | null }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const setRate = useSetRate();
  const [value, setValue] = useState("");
  const [date, setDate] = useState(today());

  useEffect(() => {
    if (!open) return;
    setValue(current ? String(current) : "");
    setDate(today());
  }, [open, current]);

  async function submit() {
    try {
      await setRate.mutateAsync({ usd_uzs: value, date });
      toast.success(t("money.rateSaved"));
      onOpenChange(false);
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("money.rateTitle")}
      description={t("money.rateDesc")}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={setRate.isPending} disabled={num(value) <= 0} onClick={() => void submit()}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("money.f.rate")}>
          <NumberInput decimals={2} min={0} value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        </Field>
        <Field label={t("money.f.date")}>
          <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Qoldiqni solishtirish (owner) ----

/** Haqiqiy karta/kassa qoldig'i kiritiladi, farq bitta "tuzatish" yozuvi bo'ladi (Bito'dagi smena yopilishi kabi, 2026-10-18) */
export function ReconcileModal({ open, onOpenChange, account }: { open: boolean; onOpenChange: (o: boolean) => void; account: MoneyAccount | null }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const reconcile = useReconcile();
  const { errors, setErrors, handle } = useErrors();
  const [actual, setActual] = useState("");
  const [date, setDate] = useState(today());
  const [time, setTime] = useState(nowTime());
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setActual("");
    setDate(today());
    setTime(nowTime());
    setNote("");
  }, [open, setErrors]);

  if (!account) return null;
  const diff = actual === "" ? null : num(actual) - num(account.balance);

  async function submit() {
    if (!account || diff === null || diff === 0) return;
    try {
      await reconcile.mutateAsync({ id: account.id, actual_balance: actual, date, time: time || undefined, note: note.trim() || undefined });
      toast.success(t("money.reconciled", { account: account.name, amount: fmt(actual, account.currency) }));
      onOpenChange(false);
    } catch (err) {
      handle(err);
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("money.reconcileTitle", { name: account.name })}
      description={t("money.reconcileDesc")}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={reconcile.isPending} disabled={diff === null || diff === 0} onClick={() => void submit()}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-[10px] bg-fill/40 px-3 py-2 text-[13px]">
          <span className="text-muted">{t("money.f.ledgerBalance")}</span> <span className="font-semibold">{fmt(account.balance, account.currency)}</span>
        </div>
        <Field label={t("money.f.actualBalance", { currency: account.currency })} required error={errors.actual_balance}>
          <NumberInput decimals={2} min={0} value={actual} onChange={(e) => setActual(e.target.value)} autoFocus />
        </Field>
        <Field label={t("money.f.date")} error={errors.date || errors.time}>
          <div className="flex gap-2">
            <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} />
            <TimeInput seconds value={time} onChange={(e) => setTime(e.target.value)} aria-label={t("money.f.time")} />
          </div>
        </Field>
        {diff !== null && (
          <p className={cn("text-[12px]", diff === 0 ? "text-muted" : diff > 0 ? "text-success" : "text-danger")}>
            {diff === 0 ? t("money.reconcileSame") : t(diff > 0 ? "money.reconcileIn" : "money.reconcileOut", { amount: fmt(Math.abs(diff), account.currency) })}
          </p>
        )}
        <Field label={t("money.f.description")}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Arxivdagi hisoblar (owner, 2026-10-25) ----

/** Arxivlangan hisoblar va "Tiklash": hisob yana ro'yxatda va jami summada; undan qilingan to'lovni bekor qilsa bo'ladi */
export function ArchivedAccountsModal({ open, onOpenChange, accounts }: { open: boolean; onOpenChange: (o: boolean) => void; accounts: MoneyAccount[] }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const restore = useRestoreAccount();
  async function run(a: MoneyAccount) {
    try {
      await restore.mutateAsync(a.id);
      toast.success(t("money.archived.restored", { name: a.name }));
      if (accounts.length <= 1) onOpenChange(false);
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
    }
  }
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={t("money.archived.title")} description={t("money.archived.desc")} size="sm">
      <div className="divide-y divide-line">
        {accounts.map((a) => (
          <div key={a.id} className="flex items-center gap-3 py-2.5">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold">{a.name}</div>
              <div className="truncate text-[12px] text-muted">
                {[t(`money.accountKinds.${a.kind}`), a.bank, a.branch_name].filter(Boolean).join(" · ")} · {fmt(a.balance, a.currency)}
              </div>
            </div>
            <Button size="sm" loading={restore.isPending && restore.variables === a.id} onClick={() => void run(a)}>
              {t("money.archived.restore")}
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
}

